/**
 * SCWT-PM-V2 Phase 6 · PWS Partner corrective-leads board.
 *
 * Route: /partner/corrective-leads (nav-linked from PartnerShell).
 * Feature-gated on `pm_corrective_lead_pipeline`.
 *
 * Behaviour:
 *   • Fetches leads assigned to this partner.
 *   • `offered` rows: only anonymised data (suburb, category summary)
 *     → Accept / Decline buttons.
 *   • Accept re-checks consent server-side and reveals contact if
 *     the landlord's `share_contact_with_partner` is still true.
 *   • Once accepted, status transitions follow the backend state
 *     machine (Contacted → Inspection booked → Quote provided →
 *     Work approved → Work completed / Not proceeding).
 *   • Availability toggle at the top of the page — pauses new
 *     offers when OFF.
 */
import { useEffect, useMemo, useState } from "react";
import {
  ShieldCheck, CheckCircle2, XCircle, Clock, Phone, Mail, User,
  Building2, MapPin, ArrowRight, Send, Info, ToggleLeft, ToggleRight,
} from "lucide-react";
import api from "@/lib/api";
import { PartnerShell } from "@/pages/partner/Dashboard";
import { useFeature } from "@/lib/useFeature";
import {
  CL_STATUS_LABEL, CL_STATUS_TONE, PARTNER_TRANSITIONS,
  offerCountdown, fmtLocal,
} from "@/lib/correctiveLeads";

export default function PartnerCorrectiveLeads() {
  const { isVisible: flagOn, loading: flagLoading } = useFeature("pm_corrective_lead_pipeline");
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [tab, setTab]             = useState("open"); // open | closed
  const [avail, setAvail]         = useState(true);
  const [busyId, setBusyId]       = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get("/partner/corrective-leads");
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load corrective leads.");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (flagOn) load(); }, [flagOn]);

  const filtered = useMemo(() => {
    const closedSet = new Set(["declined", "offer_expired", "consent_withdrawn",
      "work_completed", "not_proceeding", "withdrawn_by_pm",
      "withdrawn_by_landlord"]);
    return items.filter((r) => tab === "open"
      ? !closedSet.has(r.status)
      : closedSet.has(r.status));
  }, [items, tab]);

  const openCount   = items.filter((r) => !["declined","offer_expired","consent_withdrawn","work_completed","not_proceeding","withdrawn_by_pm","withdrawn_by_landlord"].includes(r.status)).length;
  const closedCount = items.length - openCount;

  const doAccept = async (row) => {
    setBusyId(row.id);
    try {
      await api.post(`/partner/corrective-leads/${row.id}/accept`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not accept the lead.");
    } finally { setBusyId(null); }
  };

  const doDecline = async (row) => {
    const reason = prompt("Decline this lead?\n\nOptional reason:", "");
    if (reason === null) return;
    setBusyId(row.id);
    try {
      await api.post(`/partner/corrective-leads/${row.id}/decline`,
        { reason: reason || "" });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not decline the lead.");
    } finally { setBusyId(null); }
  };

  const doStatus = async (row, new_status, extras = {}) => {
    setBusyId(row.id);
    try {
      await api.post(`/partner/corrective-leads/${row.id}/status`,
        { new_status, ...extras });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not update the lead.");
    } finally { setBusyId(null); }
  };

  const doAvailability = async (next) => {
    setAvail(next);
    try {
      await api.post("/partner/corrective-leads/availability",
        { available: next, reason: next ? "" : "off" });
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not update availability.");
      setAvail(!next);
    }
  };

  if (flagLoading) return <PartnerShell active="Corrective Leads"><div className="p-8">Loading…</div></PartnerShell>;
  if (!flagOn) {
    return (
      <PartnerShell active="Corrective Leads">
        <div data-testid="partner-corrective-leads-disabled" className="p-8 max-w-2xl">
          <ShieldCheck className="w-8 h-8 text-slate-400 mb-3" />
          <h1 className="font-display text-xl font-bold text-slate-900">Corrective-action leads</h1>
          <p className="mt-2 text-sm text-slate-600">
            This program is not enabled for your account yet. Contact your Aware admin
            to join the pilot.
          </p>
        </div>
      </PartnerShell>
    );
  }

  return (
    <PartnerShell active="Corrective Leads">
      <div data-testid="partner-corrective-leads-page" className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <header className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-cyan-700">
              <ShieldCheck className="w-3.5 h-3.5" /> PWS Partner · Warm leads
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mt-2">
              Corrective-action leads
            </h1>
            <p className="mt-1.5 text-sm text-slate-600 max-w-2xl leading-relaxed">
              Property managers refer landlords whose report recommends
              corrective work. Landlord contact details unlock <em>after</em>
              you accept — and only if the landlord&apos;s consent is active.
              Quotes stay in your existing tools; here you just track
              progress.
            </p>
          </div>
          <button
            type="button"
            data-testid="partner-corrective-leads-availability-toggle"
            onClick={() => doAvailability(!avail)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-2 text-xs font-semibold ${
              avail
                ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                : "bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200"
            }`}
          >
            {avail
              ? <><ToggleRight className="w-4 h-4" /> Accepting new leads</>
              : <><ToggleLeft className="w-4 h-4" /> Paused</>}
          </button>
        </header>

        <div className="flex items-center gap-1 mb-5 text-sm">
          <TabPill testid="partner-corrective-leads-tab-open"
                   active={tab === "open"} onClick={() => setTab("open")}
                   label={`Open · ${openCount}`} />
          <TabPill testid="partner-corrective-leads-tab-closed"
                   active={tab === "closed"} onClick={() => setTab("closed")}
                   label={`Closed · ${closedCount}`} />
        </div>

        {loading && <div className="text-sm text-slate-500 py-10 text-center">Loading…</div>}

        {!loading && error && (
          <div data-testid="partner-corrective-leads-error"
               className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
            {error}
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div data-testid="partner-corrective-leads-empty"
               className="rounded-3xl border border-dashed border-slate-200 bg-white p-10 text-center">
            <Info className="w-8 h-8 text-slate-400 mx-auto mb-3" />
            <div className="font-display font-semibold text-slate-900">
              {tab === "open" ? "No open leads right now" : "No closed leads yet"}
            </div>
            <p className="text-slate-500 text-sm mt-1.5">
              We&apos;ll notify you when a property manager sends a new lead your way.
            </p>
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <ul data-testid="partner-corrective-leads-list" className="grid gap-3">
            {filtered.map((row) => (
              <PartnerLeadCard
                key={row.id}
                row={row}
                busy={busyId === row.id}
                onAccept={() => doAccept(row)}
                onDecline={() => doDecline(row)}
                onStatus={(s, x) => doStatus(row, s, x)}
              />
            ))}
          </ul>
        )}
      </div>
    </PartnerShell>
  );
}

function TabPill({ label, active, onClick, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`rounded-full px-4 py-1.5 font-semibold ${
        active ? "bg-cyan-700 text-white" : "text-slate-600 hover:bg-slate-100"
      }`}
    >{label}</button>
  );
}

function PartnerLeadCard({ row, busy, onAccept, onDecline, onStatus }) {
  const tone = CL_STATUS_TONE[row.status] || "bg-slate-100 text-slate-700 border-slate-200";
  const label = CL_STATUS_LABEL[row.status] || row.status;
  const anonymised = row.status === "offered";
  const allowedNext = PARTNER_TRANSITIONS[row.status] || [];
  const contact = row.landlord_contact_snapshot;
  const consentActive = !!row.landlord_consent_confirmed;
  const propSnap = row.property_snapshot || {};

  return (
    <li
      data-testid={`partner-corrective-lead-${row.id}`}
      className="bg-white rounded-2xl border border-slate-200 hover:border-cyan-200 hover:shadow transition-all p-4 sm:p-5"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-xs sm:text-sm text-slate-500">{row.reference}</span>
        <span
          data-testid={`partner-corrective-lead-status-${row.id}`}
          className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider rounded-full border px-2.5 py-1 ${tone}`}
        >
          {label}
        </span>
        {row.status === "offered" && (
          <span
            data-testid={`partner-corrective-lead-countdown-${row.id}`}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700"
          >
            <Clock className="w-3 h-3" /> {offerCountdown(row.offer_expires_at)}
          </span>
        )}
      </div>

      {anonymised ? (
        <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Suburb</div>
            <div className="mt-0.5 text-slate-900 font-semibold inline-flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5 text-slate-400" />
              {row.suburb_only || "—"}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Water source</div>
            <div className="mt-0.5 text-slate-900 font-semibold">{row.water_source || "—"}</div>
          </div>
          {row.recommended_actions_summary?.length > 0 && (
            <div className="sm:col-span-2">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Recommended actions</div>
              <div className="mt-1 flex flex-wrap gap-1">
                {row.recommended_actions_summary.map((a, i) => (
                  <span
                    key={i}
                    className={`text-[11px] rounded-full px-2 py-0.5 border ${
                      a.severity === "high" ? "bg-rose-50 text-rose-800 border-rose-200"
                      : a.severity === "medium" ? "bg-amber-50 text-amber-800 border-amber-200"
                      : "bg-slate-50 text-slate-700 border-slate-200"}`}
                  >
                    {a.code || a.category || "action"}
                    {a.requires_site_visit && " · site"}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div
            data-testid={`partner-corrective-lead-consent-${row.id}`}
            className={`sm:col-span-2 rounded-xl border p-3 text-xs ${
              consentActive
                ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                : "bg-amber-50 border-amber-200 text-amber-800"}`}
          >
            <ShieldCheck className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
            {consentActive
              ? "Landlord has consented to share contact — you'll see it after accepting."
              : "Landlord has NOT consented to share contact. Accepting doesn't unlock contact until they opt in."}
          </div>
          <div className="sm:col-span-2 flex items-center gap-2 justify-end pt-1">
            <button
              type="button"
              data-testid={`partner-corrective-lead-decline-${row.id}`}
              disabled={busy}
              onClick={onDecline}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 hover:border-slate-400 text-slate-700 hover:bg-slate-50 text-sm font-semibold px-4 py-2 disabled:opacity-40"
            >
              <XCircle className="w-4 h-4" /> Decline
            </button>
            <button
              type="button"
              data-testid={`partner-corrective-lead-accept-${row.id}`}
              disabled={busy}
              onClick={onAccept}
              className="inline-flex items-center gap-1.5 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold px-4 py-2 shadow-sm disabled:opacity-50"
            >
              <CheckCircle2 className="w-4 h-4" /> Accept &amp; view contact
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-3 grid gap-3">
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Property</div>
              <div className="mt-0.5 text-slate-900 font-semibold inline-flex items-center gap-1">
                <Building2 className="w-3.5 h-3.5 text-slate-400" />
                {propSnap.address?.full || propSnap.address?.suburb || row.suburb_only || "—"}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Accepted at</div>
              <div className="mt-0.5 text-slate-700">{fmtLocal(row.accepted_at)}</div>
            </div>
          </div>
          {contact ? (
            <div
              data-testid={`partner-corrective-lead-contact-${row.id}`}
              className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900"
            >
              <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">
                Landlord contact (consent active)
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                {contact.name  && (<span className="inline-flex items-center gap-1"><User  className="w-3.5 h-3.5" />{contact.name}</span>)}
                {contact.email && (<a href={`mailto:${contact.email}`} className="inline-flex items-center gap-1 underline"><Mail  className="w-3.5 h-3.5" />{contact.email}</a>)}
                {contact.phone && (<a href={`tel:${contact.phone}`} className="inline-flex items-center gap-1 underline"><Phone className="w-3.5 h-3.5" />{contact.phone}</a>)}
              </div>
            </div>
          ) : (
            <div
              data-testid={`partner-corrective-lead-contact-pending-${row.id}`}
              className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800"
            >
              Landlord consent to share contact is not currently active.
              Ask the property manager to re-request contact-sharing consent
              from the landlord.
            </div>
          )}

          {allowedNext.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">
                Update status
              </div>
              <div className="flex flex-wrap gap-2">
                {allowedNext.map((s) => (
                  <StatusButton
                    key={s}
                    testid={`partner-corrective-lead-status-btn-${row.id}-${s}`}
                    label={CL_STATUS_LABEL[s] || s}
                    disabled={busy}
                    onClick={() => {
                      if (s === "quote_provided") {
                        const ref = prompt("External quote reference (optional):", "");
                        const sys = prompt("Quote system (e.g. ServiceM8) — optional:", "");
                        onStatus(s, {
                          quote_reference: ref || undefined,
                          external_quote_system: sys || undefined,
                        });
                      } else onStatus(s);
                    }}
                  />
                ))}
              </div>
            </div>
          )}
          {row.optional_pm_message && (
            <div className="text-xs text-slate-600">
              <span className="font-semibold text-slate-800">Property manager note:</span> {row.optional_pm_message}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function StatusButton({ label, onClick, disabled, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      disabled={disabled}
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-300 hover:border-cyan-300 text-slate-700 hover:text-cyan-800 text-xs font-semibold px-3 py-1.5 disabled:opacity-40"
    >
      <ArrowRight className="w-3 h-3" /> {label}
    </button>
  );
}
